import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../state/store";
import { Lead } from "../../models/Lead";
import { useEffect, useMemo, useState } from "react";
import { User } from "../../models/User";
import { DocumentEnvelope } from "../../models/ConsentDocument";
import { SubmitHandler, useForm } from "react-hook-form";
import { Toast } from "../../components/Toast";
import { RoleEnum } from "../../enums/Role";
import { BackUpOptions } from "../../enums/BackupOptions";
import states from "states-us";
import { fetchLeadDocumentEnvelopes, getEnvelopeDownloadDocument } from "../../state/consentDocuments/actions";
import { deleteFollowUpMessageExcecution, fetchLeadAutomations } from "../../state/automations/action";
import { ClientLeadFrequencyAutomation } from "../../models/FollowUp";
import { LoadingMask } from "../../components/LoadingMask";
import { toast, ToastContainer } from "react-toastify";
import { checkLeadPhoneNumber, createLead, updateLead } from "../../state/leads/actions";
import { LeadsPath, NewClientPath } from "../../constants/Routes";
import { longLocalDateFormat, shortDateFormat } from "../../utils/functions";
import Button from "../../components/Button";
import { DocumentArrowDownIcon, PauseIcon } from "@heroicons/react/24/solid";
import { CheckIcon, NoSymbolIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import { Form } from "../../components/form/Form";
import { ReactiveFormInput } from "../../components/form/ReactiveFormInput";
import { ReactiveFormPhoneInput } from "../../components/form/ReactiveFormPhoneInput";
import { ReactiveFormSelect } from "../../components/form/ReactiveFormSelect";
import { getCountries } from "../../i18n/countries";
import { getLeadStatuses } from "../../enums/LeadStatus";
import { getSources } from "../../enums/Source";
import { ReactiveFormRemoteSearchableSelect } from "../../components/form/ReactiveFormRemoteSearchableSelect";
import { searchCampaigns } from "../../state/campaigns/actions";
import { LeadAutomationsModule } from "../../components/leads/automations/LeadAutomationsModules";
import { LeadConsentDocumentsModule } from "../../components/leads/consentDocuments/LeadConsentDocumentsModule";
import { ClientInformationAssignAgentModal } from "../../components/clients/ClientInformationAssignAgentModal";
import { LeadInformationModal } from "../../components/leads/LeadInformationModal";
import { LeadExistsConfirmationModal } from "../../components/leads/LeadExistsConfirmationModal";

export const LeadForm = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const currentUser = useSelector((state: any) => state.users.currentUser);
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    
    const stateLead: Lead | null = location.state ? location.state.lead ?? null : null;
    const isNewLead = stateLead === null
    const [lead, setLead] = useState<Lead | null>(stateLead)

    const isLoading = useSelector((state: any) => state.leads.isLoading);
    const error = useSelector((state: any) => state.leads.error);
    const [showError, setShowError] = useState(false);
    const clients = useSelector((state: any) => state.clients.clients);

    const [selectedCampaign, setSelectedCampaign] = useState<any | null>(null);
    const [loadingCampaign, setLoadingCampaign] = useState(false);
    const users = useSelector((state: any) => state.users.users);
    const backupOfAgents = useSelector((state: RootState) => state.users.backupOfAgents);
    const [canCreateToAnother, setCanCreateToAnother] = useState(false)
    const [displayAgents, setDisplayAgents] = useState<User[]>([])
    const [assignToAgent, setAssignToAgent] = useState<any>(null);

    const [clientInformationAssignAgentModal, setClientInformationAssignAgentModal] = useState<{
        show: boolean;
        agents?: any;
    }>({ show: false, agents: [] });
    
    const [leadPhoneNumber, setLeadPhoneNumber] = useState(null);
    const [isPhoneNumberEnter, setIsPhoneNumberEnter] = useState(false);
    const [leadPhoneAlreadyExist, setLeadPhoneAlreadyExist] = useState(false);
    const [leadExistsWithPhone, setLeadExistsWithPhone] = useState<Lead>();
    const [phoneNumberExist, setPhoneNumberExist] = useState<string>();
    const [newLeadContinueData, setNewLeadContinueData] = useState<Lead>();
    const [canEditLead, setCanEditLead] = useState<boolean>(true)
    
    const [showLeadInformationModal, setShowLeadInformationModal] = useState<{
        lead?: Lead;
        currentUser?: any;
        show: boolean;
    }>({ show: false, lead: undefined });
    
    const [showLeadExistsConfirmationModal, setShowLeadExistsConfirmationModal] = useState<{
        leadExists?: Lead;
        phoneExists?: string;
        show: boolean;
    }>({ show: false, leadExists: undefined });

    useEffect(() => {
        if (showError) {
          Toast(t(error?.reason ?? "SOMETHING_WENT_WRONG"), t(error?.cause_info));
          setShowError(false);
        }
    }, [showError, error, t]);

    const methods = useForm<Lead>({
        defaultValues: lead !== null ? lead : { country: "US" }
    });

    const { handleSubmit, control, setValue } = methods;

    useEffect(() => {
        if(lead && !isNewLead) {
            setCanCreateToAnother(false)
            setDisplayAgents([])
            if(currentUser && currentUser?.role_id === RoleEnum.AGENT) {
              if(lead.agent_id !== currentUser?.id) {
                if(currentUser?.agency.backup_config_id === BackUpOptions.ONLY_READ) {
                  setCanEditLead(false);
                } else {
                  setCanEditLead(true);
                }
              }
            }
        } else {
            setCanEditLead(true);
            if(currentUser) {
                if(currentUser?.role_id === RoleEnum.AGENT) {
                    if(currentUser?.agency) {
                        if(currentUser?.agency.backup_config_id === BackUpOptions.ONLY_READ) {
                            setCanCreateToAnother(false)
                            setDisplayAgents([])
                        } else {
                            setCanCreateToAnother(true)
                            setDisplayAgents(backupOfAgents)
                        }
                    } else {
                        setCanCreateToAnother(false)
                    }
                } else if (currentUser.role_id !== RoleEnum.AGENT) {
                    setCanCreateToAnother(true)
                    setDisplayAgents(users)
                }
            }
        }
    }, [lead, users, backupOfAgents, currentUser])

    const getStates = useMemo(() => {
        return states.map((state: { name: any; abbreviation: any }) => {
          return {
            label: state.name,
            value: state.abbreviation,
            key: state.abbreviation,
          };
        });
    }, []);

    useEffect(() => methods.setValue("campaign_id", selectedCampaign?.value), [selectedCampaign]);
    
    const onSubmit: SubmitHandler<Lead> = async (data: Lead) => {
        if(lead === null && isNewLead) {
            if (leadPhoneAlreadyExist && leadExistsWithPhone != undefined) {
                setShowLeadExistsConfirmationModal({
                    show: true,
                    leadExists: leadExistsWithPhone,
                    phoneExists: leadExistsWithPhone?.phone,
                });
                setNewLeadContinueData(data);
            } else {
                createNewLead(data);
            }
        } else {
            const updatedData = data;
            if (data.referred_amount) {
                updatedData.referred_amount = +data.referred_amount;
            }
            if (data.source_id) {
                updatedData.source_id = +data.source_id;
            }
            if (data.status_id) {
                updatedData.status_id = +data.status_id;
            }

            dispatch(updateLead({ lead: updatedData, leadId: lead!.id!})).then((e) => {
                if (e.type === "leads/updateLead/rejected") {
                    setShowError(true);
                } else {
                    toast(t("SAVED_SUCCESFULLY"));
                    navigate(LeadsPath);
                }
            });
        }
    };

    const createNewLead = async (data: Lead) => {
        const newData = data;
        if (data.referred_amount) {
          newData.referred_amount = +data.referred_amount;
        }
        if (data.source_id) {
          newData.source_id = +data.source_id;
        }
        if (data.status_id) {
          newData.status_id = +data.status_id;
        }
    
        let params;
        if(assignToAgent !== null) {
          params = {lead: newData, assignedAgentId: assignToAgent.id, currentUser: currentUser};
        } else {
          params = {lead: newData, currentUser: currentUser};
        }
    
        dispatch(createLead(params)).then((e) => {
          if (e.type === "leads/createLead/rejected") {
            setShowError(true);
          } else {
            toast(t("SAVED_SUCCESFULLY"));
            navigate(LeadsPath);
          }
        });
    }
    
    const createClient = () => {
        navigate(NewClientPath, { state: { lead: lead } });
    }

    const canEnableCreateClient = () => {
        const leadClient = clients.find((client: any) => client.lead_id === lead!.id);
        if (leadClient !== undefined) {
            return true;
        } else {
            return false;
        }
    };

    useEffect(() => {
        async function checkPhoneNumber() {
            const phone = "1" + leadPhoneNumber;
            const result = await dispatch(checkLeadPhoneNumber({ phone: phone })).unwrap();
            if (result.length > 0) {
                const lead = result[0];
                setLeadPhoneAlreadyExist(true);
                setLeadExistsWithPhone(lead);
                setPhoneNumberExist(phone);
        
                toast(t("LEAD_WITH_PHONE_NUMBER_ALREADY_EXIST"));
                window.scrollTo(0, 0);
            } else {
                setLeadPhoneAlreadyExist(false);
                setLeadExistsWithPhone(undefined);
                setPhoneNumberExist("");
            }
        
            setIsPhoneNumberEnter(false);
        }
    
        if (leadPhoneNumber != null && isPhoneNumberEnter) {
            checkPhoneNumber();
        }
    }, [leadPhoneNumber, isPhoneNumberEnter]);
    
    const handleChangePhone = (phone: any) => {
        if (phone.length == 10) {
            setLeadPhoneNumber(phone);
        }
    };
    
    const handleBlurPhone = (e: any) => {
        setIsPhoneNumberEnter(true);
    };
    
    const showLeadExistsInformation = () => {
        setShowLeadInformationModal({
          show: true,
          lead: leadExistsWithPhone,
          currentUser: currentUser,
        });
    };
    
    const handleCloseLeadExistsModal = () => {
        setShowLeadExistsConfirmationModal({
            show: false,
            leadExists: undefined,
        });
        setNewLeadContinueData(undefined);
    };
    
    const handleContinueLeadExistsModal = () => {
        setShowLeadExistsConfirmationModal({
            show: false,
            leadExists: undefined,
        });
        if (newLeadContinueData != undefined) {
            createNewLead(newLeadContinueData);
        }
    };
    
    const agents = useMemo(() => {
        return displayAgents.map((x: any) => {
          return {
            label: `${x.first_name} ${x.last_name}`,
            value: x.id,
          };
        });
    }, [displayAgents]);

    return (
      <div className="w-full grow flex flex-col  px-5 space-y-4">
        {(isLoading) && <LoadingMask />}
        <ToastContainer progressStyle={{ background: "#D4AF37" }} />
        <div className="text-black max-h-full p-5 w-screen md:w-full relative overflow-x-auto bg-white rounded-lg shadow transition-all duration-500 ease-in-out overflow-hidden">
          <div className="text-xl font-bold text-black pl-3">{t("LEAD")}</div>
          <div className="pb-5">
            <Form handleOnSubmit={handleSubmit(onSubmit)}>
              <div className="w-full text-lg font-semibold p-3">
                {t("PERSONAL_INFORMATION")}
              </div>
              <ReactiveFormInput
                control={control}
                className="md:w-1/2"
                label={t("FIRST_NAME")}
                name="first_name"
                isRequired
                isDisabled={!canEditLead}
              />
              <ReactiveFormInput
                control={control}
                className="md:w-1/2"
                label={t("LAST_NAME")}
                name="last_name"
                isRequired
                isDisabled={!canEditLead}
              />
              <ReactiveFormInput
                control={control}
                type="date"
                className="md:w-1/3"
                label={t("DATE_OF_BIRTH")}
                name="dob"
                isRequired
                isDisabled={!canEditLead}
              />
              <ReactiveFormInput
                control={control}
                className="md:w-1/3"
                label={t("EMAIL")}
                name="email"
                type="email"
                isRequired
                isDisabled={!canEditLead}
              />
              <ReactiveFormPhoneInput
                control={control}
                className="md:w-1/3"
                name="phone"
                label={t("PHONE")}
                isRequired
                handleOnChange={handleChangePhone}
                handleOnBlur={handleBlurPhone}
                isDisabled={!canEditLead}
              />
              <div className="w-full text-lg font-semibold p-3">
                {t("ADDRESS_INFORMATION")}
              </div>
              <ReactiveFormInput
                control={control}
                className="md:w-2/3"
                label={t("STREET_ADDRESS")}
                name={`address`}
                isDisabled={!canEditLead}
              />
              <ReactiveFormInput
                control={control}
                className="md:w-1/3"
                label={t("CITY")}
                name={`city`}
                isDisabled={!canEditLead}
              />
              <ReactiveFormSelect
                width="md:w-1/3"
                name={`state`}
                label={t("STATE")}
                options={getStates}
                control={control}
                isDisabled={!canEditLead}
              />
              <ReactiveFormSelect
                width="md:w-1/3"
                name="country"
                label={t("COUNTRY")}
                options={getCountries()}
                control={control}
                isDisabled={!canEditLead}
              />
              <ReactiveFormInput
                control={control}
                className="md:w-1/3"
                label={t("ZIP_CODE")}
                name={`zip_code`}
                isDisabled={!canEditLead}
              />
              <div className="w-full text-lg font-semibold p-3">
                {t("LEAD_INFORMATION")}
              </div>
              <ReactiveFormSelect
                width="md:w-1/3"
                name={`status_id`}
                label={t("STATUS")}
                options={getLeadStatuses()}
                control={control}
                isDisabled={!canEditLead}
              />
              <ReactiveFormSelect
                width="md:w-1/3"
                name={`source_id`}
                label={t("SOURCE")}
                options={getSources()}
                control={control}
                isDisabled={!canEditLead}
              />
              {
                (lead === null && isNewLead) &&
                <ReactiveFormRemoteSearchableSelect
                  control={control}
                  className="md:w-1/3"
                  label={t("CAMPAIGN")}
                  name={`campaign_id`}
                  defaultItem={selectedCampaign}
                  dataSourceAsyncThunk={searchCampaigns}
                  data={{ agencyId: currentUser?.agency_id }}
                  isDisabled={loadingCampaign}
                  onChange={(v: any) => setSelectedCampaign(v)}
                />
              }
              <ReactiveFormInput
                control={control}
                className="md:w-1/3"
                label={t("REFERRED_BY")}
                name={`referred_by`}
                isDisabled={!canEditLead}
              />
              <ReactiveFormInput
                control={control}
                className="md:w-1/3"
                label={t("AMOUNT")}
                name={`referred_amount`}
                type="number"
                isDisabled={!canEditLead}
              />
            </Form>
          </div>
        </div>

        {
          (lead === null && isNewLead) && <div className="w-full flex items-center justify-end pb-5 text-right">
            {
              (currentUser && canCreateToAnother) &&
              <div className="flex flex-col mr-5">
                <label className="grid justify-items-end text-gray-600">{`${t("AGENT_OF_RECORD")}:`}</label>
                <label
                    className="grid justify-items-end underline cursor-pointer text-lg text-sky-600"
                    onClick={() => setClientInformationAssignAgentModal({ show: true, agents: agents })}
                >
                    {   
                        assignToAgent !== null
                        ? `${assignToAgent?.first_name ?? ""} ${assignToAgent?.last_name ?? ""}`
                        : `${currentUser?.first_name ?? ""} ${currentUser?.last_name ?? ""}`
                    }
                </label>
              </div>
            }
            <Button onClick={handleSubmit(onSubmit)}>
              <span className="flex items-center font-semibold pr-3">
                <div className="w-8 p-1 aspect-square mr-2">
                  <UserPlusIcon />
                </div>
                {t("SAVE_FOR_LEADS")}
              </span>
            </Button>
          </div>
        }
  
        {
          (lead !== null && !isNewLead) && <>
            <LeadAutomationsModule lead={lead} canEditLead={canEditLead}/>
            <LeadConsentDocumentsModule lead={lead} canEditLead={canEditLead}/>
            {
              canEditLead && <div className="pb-5 pt-4 justify-end gap-4 flex">
                <Button onClick={createClient} isDisabled={canEnableCreateClient()}>
                  <span className="flex items-center font-semibold pr-3">
                    <div className="w-8 p-1 aspect-square mr-2">
                      <UserPlusIcon />
                    </div>
                    {t("CREATE_CLIENT")}
                  </span>
                </Button>
                <Button onClick={handleSubmit(onSubmit)}>
                  <span className="flex items-center font-semibold pr-3">
                    <div className="w-8 p-1 aspect-square mr-2">
                      <CheckIcon />
                    </div>
                    {t("SAVE")}
                  </span>
                </Button>
              </div>
            }
          </>
        }
        <ClientInformationAssignAgentModal
          info={clientInformationAssignAgentModal}
          onSubmited={(selectAgent) => {
              const sAgent = users.find((e: any) => e.id === selectAgent);
              if(sAgent.id === currentUser.id) {
                  setAssignToAgent(null);
              } else {
                  setAssignToAgent(sAgent);
              }
          }}
          onClose={() => setClientInformationAssignAgentModal({ show: false, agents: [] })}
        />
        <LeadInformationModal
          info={showLeadInformationModal}
          onClose={() =>
              setShowLeadInformationModal({ show: false, lead: undefined })
          }
        />
        <LeadExistsConfirmationModal
          info={showLeadExistsConfirmationModal}
          onClose={handleCloseLeadExistsModal}
          onContinueCreate={handleContinueLeadExistsModal}
        />
      </div>
    );
}